"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const FEATURES = [
  { id: "dashboard", name: "Dashboard", category: "Core" },
  { id: "risk", name: "Risk Register", category: "Registers" },
  { id: "asset", name: "Asset Register", category: "Registers" },
  { id: "docs", name: "Document Library", category: "Documents" },
  { id: "audit", name: "Audit Centre", category: "Audit" },
  { id: "calendar", name: "Compliance Calendar", category: "Planning" },
  { id: "keydocs", name: "Key Documents", category: "Documents" },
  { id: "assurance", name: "Assurance Library", category: "Audit" },
  { id: "soc2", name: "SOC2 Matrix", category: "Standards" },
  { id: "mcm", name: "Master Control Mapping", category: "Standards" },
];

export default function ModalFeatureSelectionExample() {
  const [open, setOpen] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [category, setCategory] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<string[]>([]);

  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const cats = React.useMemo(() => Array.from(new Set(FEATURES.map(f => f.category))), []);
  const filtered = React.useMemo(() => FEATURES.filter(f => {
    const q = query.trim().toLowerCase();
    const qm = !q || f.name.toLowerCase().includes(q) || f.id.includes(q);
    const cm = !category || f.category === category;
    return qm && cm;
  }), [query, category]);

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Feature Selection Modal</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl! w-6xl! bg-transparent shadow-none border-0 p-0">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full overflow-hidden shadow-2xl border border-white/10 flex flex-col" style={{ minHeight: "70vh" }}>
            <div className="p-6 pb-4 border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Feature Selection</h2>
              <p className="mt-2 text-muted-foreground">Enable or disable platform features by category.</p>
            </div>

            <div className="p-6 space-y-4 flex-1 overflow-y-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Input placeholder="Search features..." value={query} onChange={e => setQuery(e.target.value)} className="bg-white/10 border-white/20" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge onClick={() => setCategory(null)} className={cn("cursor-pointer", !category && "bg-primary/10 text-primary border-primary/30")}>All</Badge>
                  {cats.map(c => (
                    <Badge key={c} onClick={() => setCategory(c)} className={cn("cursor-pointer", category === c && "bg-primary/10 text-primary border-primary/30")}>{c}</Badge>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map(f => (
                  <label key={f.id} className={cn(
                    "flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm p-4 cursor-pointer transition-all",
                    selected.includes(f.id) && "ring-2 ring-primary/60"
                  )}>
                    <Checkbox checked={selected.includes(f.id)} onCheckedChange={() => toggle(f.id)} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-foreground font-medium">{f.name}</span>
                        <Badge variant="outline" className="text-xs">{f.category}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Toggle access to the {f.name} module.</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 px-6 py-4 bg-gradient-to-t from-primary/5 to-transparent flex justify-between items-center">
              <div className="text-sm text-muted-foreground">{selected.length} feature{selected.length !== 1 ? 's' : ''} selected</div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
                <Button onClick={() => setOpen(false)}>Apply</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
